import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { CategoryDocumentsMode } from "./api.js";

/**
 * Inline radio block used inside the delete dialogs to ask the operator
 * what should happen to the documents under a category being removed:
 *
 *   • "destroy" — cascade. Every doc is run through
 *     `Documents::PurgeAndRemove` on the Rails side — `xop-storage`
 *     attachments purged, AI Gateway vector entries dropped, doc row
 *     discarded. Files are gone from object storage.
 *   • "keep" — move to Uncategorized. The category row goes; the docs
 *     stay, just with their `document_category_id` nulled.
 *
 * Defaulting to `destroy` keeps the action consistent with what the
 * trash icon already implied in the previous build of this UI. Picking
 * `keep` is the safer fallback — exposed clearly so a slip on the
 * trash button doesn't take real documents with it.
 *
 * The component is presentational only — it doesn't know about the
 * delete itself. The parent dialog passes the selected mode back to
 * the mutation when the user confirms.
 *
 * Implementation note: the two choices stay NATIVE `<input type="radio">`
 * elements wrapped in `<label>`s so the colocated dialog specs can drive
 * them with `getByLabelText(...)`/`toBeChecked()`. Styling moves to MUI
 * `sx`; the markup contract (label-wrapped radios) is unchanged.
 */
export function CategoryDocsModePicker({
  value,
  onChange,
  documentCount,
  idPrefix,
}: {
  value: CategoryDocumentsMode;
  onChange: (next: CategoryDocumentsMode) => void;
  /**
   * Total number of documents the choice will affect. The labels read
   * differently depending on whether anything actually lives under the
   * categories being deleted.
   */
  documentCount: number;
  /** Unique radio-group name so multiple instances can coexist on a page. */
  idPrefix: string;
}): JSX.Element | null {
  // Nothing inside → no choice to make; just don't render the block.
  if (documentCount === 0) return null;

  const noun = documentCount === 1 ? "document" : "documents";

  return (
    <Box
      component="fieldset"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
        p: 1.5,
        fontSize: 14,
        m: 0,
      }}
    >
      <Box
        component="legend"
        sx={{
          px: 0.5,
          fontSize: 12,
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "text.disabled",
        }}
      >
        {`What should happen to the ${documentCount} ${noun}?`}
      </Box>

      <Box
        component="label"
        sx={[
          {
            display: "flex",
            cursor: "pointer",
            alignItems: "flex-start",
            gap: 1,
            borderRadius: 1,
            px: 1,
            py: 0.75,
            transition: "background-color 150ms",
          },
          value === "destroy"
            ? { bgcolor: (t) => alpha(t.palette.error.main, 0.08) }
            : { "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) } },
        ]}
      >
        <Box
          component="input"
          type="radio"
          name={`${idPrefix}-docs-mode`}
          value="destroy"
          checked={value === "destroy"}
          onChange={() => onChange("destroy")}
          sx={{ mt: 0.25, accentColor: "error.main" }}
        />
        <Box component="span" sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ display: "block", fontWeight: 500, color: "text.primary" }}>
            Delete {documentCount} {noun} too
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
            Files purged from object storage, vector entries removed. Cannot be
            undone.
          </Box>
        </Box>
      </Box>

      <Box
        component="label"
        sx={[
          {
            display: "flex",
            cursor: "pointer",
            alignItems: "flex-start",
            gap: 1,
            borderRadius: 1,
            px: 1,
            py: 0.75,
            transition: "background-color 150ms",
          },
          value === "keep"
            ? { bgcolor: (t) => alpha(t.palette.success.main, 0.08) }
            : { "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) } },
        ]}
      >
        <Box
          component="input"
          type="radio"
          name={`${idPrefix}-docs-mode`}
          value="keep"
          checked={value === "keep"}
          onChange={() => onChange("keep")}
          sx={{ mt: 0.25, accentColor: "success.main" }}
        />
        <Box component="span" sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ display: "block", fontWeight: 500, color: "text.primary" }}>
            Move to Uncategorized
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
            {`The ${noun} stay${documentCount === 1 ? "s" : ""} in place under the "Uncategorized" group — re-tag from each doc's edit page.`}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
